import { useEffect } from "react";
import { useRecoilCallback, useRecoilValue } from "recoil";
import { isLoggedIn } from "api/coachAuth";
import { UserSimStreamRequest } from "api/userSimApi";
import { ProgramContentType } from "data/BlockComponent";
import {
  AutoFillStreamHandle,
  INITIAL_AUTO_FILL_STATE,
  startAutoFillStream,
} from "hooks/useAutoFillStream";
import {
  AutoFillToast,
  autoFillDockOpenAtom,
  autoFillRunsAtom,
  autoFillToastsAtom,
  autoFillViewingAtom,
  getAutoFillRunKey,
} from "recoil/autoFillRunAtom";
import { userAtom } from "recoil/userAtom";

// 실행 중인 Agent 를 멈추는 손잡이 — 화면 상태가 아니라서 recoil 밖에 둔다
const handles = new Map<string, AutoFillStreamHandle>();
let lastToastId = 0;

// 웹 자동 채우기 — 실행 상태는 전역(recoil/autoFillRunAtom), 활동지를 나가도 끊지 않는다.
// 끊는 경우: [중지], 60초 무응답(실패로 끝남), 로그아웃(useCancelAutoFillOnLogout)
export default function useAutoFillRuns() {
  const pushToast = useRecoilCallback(
    ({ set }) =>
      (toast: Omit<AutoFillToast, "id">) =>
        // 같은 활동지 알림은 새 것으로 바꾼다
        set(autoFillToastsAtom, (prev) => [
          ...prev.filter((each) => each.runKey !== toast.runKey),
          { ...toast, id: ++lastToastId },
        ]),
    []
  );

  const closeToast = useRecoilCallback(
    ({ set }) =>
      (id: number) =>
        set(autoFillToastsAtom, (prev) =>
          prev.filter((each) => each.id !== id)
        ),
    []
  );

  // 실행과 그 알림을 지운다 — 넣었거나, 넣지 못해 확인했거나, 진행 표시에서 닫았을 때
  const remove = useRecoilCallback(
    ({ set }) =>
      (key: string) => {
        handles.delete(key);
        set(autoFillRunsAtom, (prev) => {
          const { [key]: _removed, ...rest } = prev;
          return rest;
        });
        set(autoFillToastsAtom, (prev) =>
          prev.filter((each) => each.runKey !== key)
        );
      },
    []
  );

  // 끝났을 때 그 활동지가 화면에 없으면 알린다 — 화면에 있으면 AutoFillButton 이 바로 넣는다
  const settle = useRecoilCallback(
    ({ snapshot, set }) =>
      (key: string, handle: AutoFillStreamHandle) => {
        if (handles.get(key) === handle) handles.delete(key);
        const run = snapshot.getLoadable(autoFillRunsAtom).getValue()[key];
        if (!run || (run.phase !== "done" && run.phase !== "error")) return;
        if (snapshot.getLoadable(autoFillViewingAtom).getValue() === key) {
          return;
        }
        pushToast({
          runKey: key,
          title: run.title,
          ...(run.phase === "done"
            ? { kind: "ready", message: "자동 채우기가 완료되었어요" }
            : {
                kind: "error",
                message: run.error || "자동 채우기를 하지 못했어요",
              }),
        });
      },
    [pushToast]
  );

  // 같은 활동지는 한 번에 하나만 돈다. 여러 활동지는 동시에 돌 수 있다
  const start = useRecoilCallback(
    ({ snapshot, set }) =>
      ({
        taskKey,
        thoughtRecordKey,
        title,
        baseContent,
        getRequest,
      }: {
        taskKey: string;
        thoughtRecordKey?: string;
        title: string;
        baseContent: ProgramContentType[];
        getRequest: () => Promise<UserSimStreamRequest>;
      }) => {
        const key = getAutoFillRunKey(taskKey, thoughtRecordKey);
        if (
          handles.has(key) ||
          snapshot.getLoadable(autoFillRunsAtom).getValue()[key]
        ) {
          return;
        }
        set(autoFillRunsAtom, (prev) => ({
          ...prev,
          [key]: {
            ...INITIAL_AUTO_FILL_STATE,
            key,
            taskKey,
            thoughtRecordKey,
            title,
            startedAt: Date.now(),
            baseContent,
          },
        }));
        // 지운 뒤 늦게 온 이벤트로 되살리지 않는다
        const handle = startAutoFillStream(getRequest, (updater) =>
          set(autoFillRunsAtom, (prev) =>
            prev[key]
              ? { ...prev, [key]: { ...prev[key], ...updater(prev[key]) } }
              : prev
          )
        );
        handles.set(key, handle);
        handle.done.then(() => settle(key, handle));
      },
    [settle]
  );

  // "중지하고 있어요" 를 보인 뒤, 멈추면 알림 없이 지운다
  const cancel = useRecoilCallback(
    ({ set }) =>
      async (key: string) => {
        const handle = handles.get(key);
        set(autoFillRunsAtom, (prev) =>
          prev[key]
            ? { ...prev, [key]: { ...prev[key], phase: "canceling" as const } }
            : prev
        );
        await handle?.cancel();
        remove(key);
      },
    [remove]
  );

  // 로그아웃 — 모두 끊고 비운다
  const cancelAll = useRecoilCallback(
    ({ set }) =>
      () => {
        handles.forEach((handle) => handle.cancel());
        handles.clear();
        set(autoFillRunsAtom, {});
        set(autoFillToastsAtom, []);
        set(autoFillDockOpenAtom, false);
      },
    []
  );

  return { start, cancel, remove, cancelAll, pushToast, closeToast };
}

// 로그아웃하거나 다른 계정으로 바뀌면 모두 끊는다. 웹에서 늘 떠 있는 곳(진행 표시)에서 부른다
export function useCancelAutoFillOnLogout() {
  const user = useRecoilValue(userAtom);
  const loggedIn = isLoggedIn(user);
  const userId = user?.userId;
  const { cancelAll } = useAutoFillRuns();
  // 로그인 상태·계정이 바뀌면 이전 계정의 실행을 정리한다 (cleanup)
  useEffect(() => cancelAll, [loggedIn, userId, cancelAll]);
}
